import { BaseSerializer } from '@core/serializers/baseSerializer';

/**
 * PackageSerializer V3
 * Transformation API <-> UI pour toutes les opérations packages
 *
 * L'API nouvelle génération utilise le format :
 *   { statusCode: 200|201, message: "...", data: { ... } }
 *
 * La structure de payload (création) a aussi changé :
 *   - pickup / destination (lat/lng au lieu de coordinates)
 *   - packageInfo.weight: { value, unit }
 *   - payment.method
 *   - Pas de srcDstLocation imbriqué
 */
class PackageSerializer extends BaseSerializer {
  // ============================================
  // SERIALIZE: UI -> API  (nouveau format v2)
  // ============================================

  /**
   * Serialize fees calculation payload for API
   * POST /package/calculate/fee
   *
   * Attendu :
   * {
   *   metadata:    { wallet, merchandisePrice, deliveryFeePayer, paymentLinkEnabled },
   *   distanceKm,
   *   pickup:      { latitude, longitude },
   *   destination: { latitude, longitude },
   *   checkpoints: [ { address, latitude, longitude, contactName, phone, description } ],
   *   packageInfo: { quantity, weight: { value, unit } }
   * }
   */
  serializeFeesCalculation(data) {
    if (!data) {
      throw new Error('Fees calculation data is required');
    }

    const srcDstLocation = data.srcDstLocation || {};
    const packageType = data.packageType || {};
    const metadata = data.metadata || {};

    // distanceKm est calculé en amont par le controller (via getRouteDistance)
    // On le lit directement sur data
    const distanceKm = data.distanceKm || 0;

    // Poids : supporte l'ancien { value, unit } ou le flat { weight, unit }
    const parcelDetails = packageType.parcelDetails || data.parcelDetails || {};
    const rawWeight = parcelDetails.weight || packageType.weight || data.weight;
    const weightValue =
      typeof rawWeight === 'object'
        ? rawWeight.value || 0
        : Number(rawWeight) || 0;
    const weightUnit =
      parcelDetails.unit ||
      (typeof rawWeight === 'object' ? rawWeight.unit : null) ||
      packageType.unit ||
      'kg';

    const userId =
      data.userId ||
      (data.walletId ? data.walletId.replace('wallet_', '') : '');
    const wallet =
      metadata.wallet ||
      data.wallet ||
      data.walletId ||
      (userId
        ? userId.startsWith('wallet_')
          ? userId
          : `wallet_${userId}`
        : '');

    const merchandisePrice = Number(
      data.merchandisePrice ?? metadata.merchandisePrice ?? 0
    );
    const deliveryFeePayer =
      data.deliveryFeePayer ?? metadata.deliveryFeePayer ?? 'sender';
    const paymentLinkEnabled = Boolean(
      data.paymentLinkEnabled ?? metadata.paymentLinkEnabled ?? false
    );

    // Checkpoints (points de passage intermédiaires) — même format que serializeDeliveryCreation
    const checkpoints = (srcDstLocation.checkpoints || []).map((cp) => ({
      address: cp.address || cp.locationText || '',
      latitude: cp.coordinates?.lat ?? cp.latitude,
      longitude: cp.coordinates?.lng ?? cp.longitude,
      contactName: cp.contactName || '',
      phone: cp.contactPhone || cp.phone || '',
      // Description de localisation du checkpoint (indications précises pour le rider)
      description: cp.description || ''
    }));

    return {
      metadata: {
        wallet,
        merchandisePrice,
        deliveryFeePayer,
        paymentLinkEnabled
      },
      distanceKm,
      pickup: {
        latitude:
          srcDstLocation.srcPosition?.coordinates?.lat ??
          srcDstLocation.srcPosition?.latitude,
        longitude:
          srcDstLocation.srcPosition?.coordinates?.lng ??
          srcDstLocation.srcPosition?.longitude
      },
      destination: {
        latitude:
          srcDstLocation.dstPosition?.coordinates?.lat ??
          srcDstLocation.dstPosition?.latitude,
        longitude:
          srcDstLocation.dstPosition?.coordinates?.lng ??
          srcDstLocation.dstPosition?.longitude
      },
      checkpoints,
      packageInfo: {
        quantity:
          parcelDetails.quantity ||
          packageType.nbDocs ||
          packageType.quantity ||
          data.quantity ||
          1,
        weight: {
          value: weightValue,
          unit: (weightUnit || 'kg').toLowerCase()
        }
      }
    };
  }

  /**
   * Serialize delivery creation payload for API
   * POST /package/create
   *
   * Attendu :
   * {
   *   userId, title, distanceKm,
   *   pickup: { address, latitude, longitude, contactName, phone },
   *   destination: { address, latitude, longitude, contactName, phone },
   *   packageInfo: { description, category, quantity, weight: { value, unit },
   *                  fragile, value, pickupMethod, pickupScheduledAt, metadata },
   *   payment: { method }
   * }
   */
  serializeDeliveryCreation(data) {
    if (!data) {
      throw new Error('Delivery creation data is required');
    }

    const srcDstLocation = data.srcDstLocation || {};
    const packageType = data.packageType || {};
    const deliveryType = data.deliveryType || {};
    const fees = data.fees || {};

    const src = srcDstLocation.srcPosition || {};
    const dst = srcDstLocation.dstPosition || {};

    // Aplatissement de parcelDetails → packageInfo
    const parcelDetails = packageType.parcelDetails || {};
    const rawWeight = parcelDetails.weight || packageType.weight;
    const weightValue =
      typeof rawWeight === 'object' ? rawWeight.value || 0 : rawWeight || 0;
    const weightUnit =
      parcelDetails.unit ||
      (typeof rawWeight === 'object' ? rawWeight.unit : null) ||
      'kg';

    // Checkpoints (points de passage intermédiaires)
    const checkpoints = (srcDstLocation.checkpoints || []).map((cp) => ({
      address: cp.address || cp.locationText || '',
      latitude: cp.coordinates?.lat || cp.latitude,
      longitude: cp.coordinates?.lng || cp.longitude,
      contactName: cp.contactName || '',
      phone: cp.contactPhone || cp.phone || '',
      // Description de localisation du checkpoint (indications précises pour le rider)
      description: cp.description || ''
    }));
    let paymentMethod = data.paymentMethod || 'cash';
    // if (paymentMethod == 'mobile_money') paymentMethod = 'mobileMoney';

    return {
      userId: data.userId,
      title: data.title || 'Livraison de colis',
      distanceKm: fees.totalDistance || data.distanceKm || 0,
      durationMin: fees.totalDuration || data.durationMin || 0,
      notes: data.notes || '',

      pickup: {
        address: src.address || src.locationText,
        latitude: src.coordinates?.lat,
        longitude: src.coordinates?.lng,
        contactName: src.contactName,
        phone: src.contactPhone,
        // Description de localisation du pickup (indications précises pour le rider)
        description: src.description || ''
      },

      checkpoints,

      destination: {
        address: dst.address || dst.locationText,
        latitude: dst.coordinates?.lat,
        longitude: dst.coordinates?.lng,
        contactName: dst.contactName,
        phone: dst.contactPhone,
        // Description de localisation de la destination (indications précises pour le rider)
        description: dst.description || ''
      },

      packageInfo: {
        description: parcelDetails.description || packageType.description,
        category: packageType.type || parcelDetails.category || 'package',
        quantity: parcelDetails.quantity || packageType.nbDocs || 1,
        weight: {
          value: weightValue,
          unit: weightUnit.toLowerCase()
        },
        dimensions: parcelDetails.dimensions || null,
        fragile: parcelDetails.fragile ?? false,
        value: parcelDetails.value || 0,
        pickupMethod: deliveryType.deliveryType || 'motorcycle',
        pickupScheduledAt: data.startDate || null,
        metadata: {
          requiresSignature: parcelDetails.requiresSignature ?? false
        }
      },

      // Bloc payment complet — le serveur déclenche le paiement
      payment: {
        status: 'pending',
        method: paymentMethod,
        operator: data.paymentOperator || '', // MTN | MOOV | CELTIS
        provider: data.paymentProvider || '', // fedapay | cinetpay | wallet | cash
        phoneNumber: data.paymentPhone || '', // Mobile Money uniquement
        walletId: data.walletId || '' // Wallet uniquement
      },

      // Métadonnées escrow (lien de paiement sécurisé)
      // + locationsFull : réplique des adresses avec descriptions complètes
      // (fallback car l'API peut ne pas persister `description` dans pickup/destination/checkpoints)
      metadata: {
        wallet: `wallet_${data.userId}`,
        merchandisePrice: data.merchandisePrice || 0,
        deliveryFeePayer: data.deliveryFeePayer || 'sender', // 'sender' | 'receiver'
        paymentLinkEnabled: data.paymentLinkEnabled || false,
        locationsFull: {
          pickup: {
            address: src.address || src.locationText || '',
            latitude: src.coordinates?.lat,
            longitude: src.coordinates?.lng,
            contactName: src.contactName || '',
            phone: src.contactPhone || '',
            description: src.description || ''
          },
          destination: {
            address: dst.address || dst.locationText || '',
            latitude: dst.coordinates?.lat,
            longitude: dst.coordinates?.lng,
            contactName: dst.contactName || '',
            phone: dst.contactPhone || '',
            description: dst.description || ''
          },
          checkpoints: (srcDstLocation.checkpoints || []).map((cp) => ({
            address: cp.address || cp.locationText || '',
            latitude: cp.coordinates?.lat || cp.latitude,
            longitude: cp.coordinates?.lng || cp.longitude,
            contactName: cp.contactName || '',
            phone: cp.contactPhone || cp.phone || '',
            description: cp.description || ''
          }))
        }
      }
    };
  }

  // ============================================
  // DESERIALIZE: API -> UI  (nouveau format v2)
  // ============================================

  /**
   * Deserialize packages list response from API
   * GET /package/list → { statusCode, data: [...] }
   */
  deserializeList(raw) {
    // Le wrapper réseau retourne { success, data: <API response> }
    // La réponse API est { statusCode, data: [...] }
    const apiResponse = raw?.data || raw;
    const packagesList = Array.isArray(apiResponse?.data)
      ? apiResponse.data
      : Array.isArray(apiResponse)
        ? apiResponse
        : [];

    return packagesList.map((pkg) => this.deserializePackage(pkg));
  }

  /**
   * Deserialize single package from API (nouveau format)
   * Champs principaux : id, reference, userId, title, status,
   *   pickup, destination, currentLocation, packageInfo, timeline,
   *   distanceKm, pricing, payment, notes, createdAt, updatedAt
   */
  deserializePackage(apiData) {
    if (!apiData) return null;

    // 1. Sécurisation des objets imbriqués pour éviter les crashs si l'API renvoie null
    const pickup = apiData.pickup || {};
    const destination = apiData.destination || {};
    const packageInfo = apiData.packageInfo || {};
    const pricing = apiData.pricing || {};
    const payment = apiData.payment || {};
    const timeline = Array.isArray(apiData.timeline) ? apiData.timeline : [];

    // Fallback descriptions : metadata.locationsFull (conservé même si l'API strip
    // `description` des niveaux pickup/destination/checkpoints)
    const locationsFull = apiData.metadata?.locationsFull || {};
    const pickupFull = locationsFull.pickup || {};
    const destinationFull = locationsFull.destination || {};
    const checkpointsFull = locationsFull.checkpoints || [];

    let checkpoints = Array.isArray(apiData.checkpoints)
      ? apiData.checkpoints
      : [];

    checkpoints = checkpoints.map((cp, i) => {
      let isReached = timeline.some(
        (event) =>
          event.checkpointId === cp._id && event.status === 'checkpoint_reached'
      );
      let isDelivered = timeline.some(
        (event) =>
          event.checkpointId === cp._id &&
          event.status === 'checkpoint_delivered'
      );
      let reachedAt = timeline.find(
        (event) =>
          event.checkpointId === cp._id && event.status === 'checkpoint_reached'
      )?.timestamp;
      let deliveredAt = timeline.find(
        (event) =>
          event.checkpointId === cp._id &&
          event.status === 'checkpoint_delivered'
      )?.timestamp;

      return {
        ...cp,
        // Description : champ direct API → fallback metadata.locationsFull.checkpoints[i]
        description: cp.description || checkpointsFull[i]?.description || '',
        isReached,
        isDelivered,
        reachedAt,
        deliveredAt
      };
    });

    // 2. --- NOUVELLE LOGIQUE : Calcul de progression basé sur la timeline ---
    const sortedTimeline = [...timeline].sort(
      (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
    );
    const latestEvent =
      sortedTimeline.length > 0
        ? sortedTimeline[sortedTimeline.length - 1]
        : null;

    const currentTimelineStatus = latestEvent
      ? latestEvent.status
      : apiData.status || 'created';
    let currentStatusLabel = 'Enregistré';
    let progressPercentage = 0;

    if (
      apiData.status === 'canceled' ||
      currentTimelineStatus === 'cancelled'
    ) {
      currentStatusLabel = 'Annulé';
      progressPercentage = 0;
    } else if (
      apiData.status === 'delivered' ||
      currentTimelineStatus === 'delivered'
    ) {
      currentStatusLabel = 'Livré';
      progressPercentage = 100;
    } else {
      switch (currentTimelineStatus) {
        case 'created':
          currentStatusLabel = 'Colis enregistré';
          progressPercentage = 10;
          break;
        case 'confirmed':
        case 'payment_paid':
          currentStatusLabel = 'Confirmé';
          progressPercentage = 20;
          break;
        case 'assigned':
          currentStatusLabel = 'Livreur assigné';
          progressPercentage = 30;
          break;
        case 'picked_up':
          currentStatusLabel = 'Colis récupéré';
          progressPercentage = 50;
          break;
        case 'in_transit':
          currentStatusLabel = 'En transit';
          progressPercentage = 60;
          break;
        case 'checkpoint_reached':
        case 'checkpoint_delivered':
          currentStatusLabel =
            latestEvent.description || 'À un point de contrôle';
          const totalCheckpoints = checkpoints.length || 1;
          const currentPos =
            latestEvent.metadata?.checkpoint?.checkpointPosition || 0;
          const checkpointRatio = (currentPos + 1) / totalCheckpoints;
          progressPercentage = 50 + Math.floor(checkpointRatio * 35);
          break;
        case 'arrived':
          currentStatusLabel = 'Arrivé à destination';
          progressPercentage = 95;
          break;
        default:
          currentStatusLabel = 'En cours';
          progressPercentage = 40;
      }
    }

    // 3. --- HISTORIQUE DES DATES ---
    const stepsTimeline = {
      confirmed:
        timeline.find((t) => t.status === 'confirmed')?.timestamp || null,
      pickedUp:
        timeline.find((t) => t.status === 'picked_up')?.timestamp || null,
      inTransit:
        timeline.find((t) => t.status === 'in_transit')?.timestamp || null,
      delivered:
        timeline.find((t) => t.status === 'delivered')?.timestamp || null,
      canceled:
        timeline.find(
          (t) => t.status === 'cancelled' || t.status === 'canceled'
        )?.timestamp || null,
      payment:
        timeline.find((t) => t.status === 'payment_paid')?.timestamp || null
    };

    // 4. --- RETOUR DE L'OBJET CORRIGÉ ---
    return {
      // === IDENTITÉ DE BASE ===
      id: apiData.id || apiData._id,
      reference: apiData.reference,
      trackingCode: apiData.reference,
      deliveryCode: apiData.deliveryCode?.includes('-')
        ? apiData.deliveryCode.split('-')[1]
        : apiData.deliveryCode,
      title: apiData.title || packageInfo.title || '',
      userId: apiData.userId,
      riderId: apiData.riderId,
      rider: {
        name: apiData.rider?.firstName
          ? `${apiData.rider?.firstName} ${apiData.rider?.lastName}`
          : '',
        riderId: apiData.riderId,
        walletId: apiData.rider?.walletId,
        firstName: apiData.rider?.firstName,
        lastName: apiData.rider?.lastName,
        phone: apiData.rider?.phone,
        vehicle: apiData.rider?.vehicle
      },

      // === STATUTS & PROGRESSION ===
      status: apiData.status,
      currentTimelineStatus: currentTimelineStatus,
      currentStatusLabel: currentStatusLabel,
      progressPercentage: progressPercentage,
      latestTimelineDescription: latestEvent?.description || '',
      durationMin: apiData.durationMin || null,

      // === INFORMATIONS COLIS ===
      packageInfo: {
        category: packageInfo.category || '',
        weight: packageInfo.weight || null,
        dimensions: packageInfo.dimensions || null,
        description: packageInfo.description || '',
        instructions: packageInfo.instructions || '',
        isFragile: packageInfo.isFragile || false,
        images: packageInfo.images || []
      },

      // === ADRESSES & CONTACTS ===
      pickup: {
        address: pickup.address || '',
        name: pickup.contactName || pickup.name || '',
        phone: pickup.phone || '',
        // Description : champ direct API → fallback metadata.locationsFull.pickup
        description: pickup.description || pickupFull.description || '',
        latitude: pickup.latitude != null ? pickup.latitude : undefined,
        longitude: pickup.longitude != null ? pickup.longitude : undefined,
        coordinates:
          pickup.coordinates ||
          (pickup.latitude != null && pickup.longitude != null
            ? { lat: pickup.latitude, lng: pickup.longitude }
            : null),
        date: pickup.date || null
      },
      destination: {
        address: destination.address || '',
        name: destination.contactName || destination.name || '',
        phone: destination.phone || '',
        // Description : champ direct API → fallback metadata.locationsFull.destination
        description: destination.description || destinationFull.description || '',
        latitude:
          destination.latitude != null ? destination.latitude : undefined,
        longitude:
          destination.longitude != null ? destination.longitude : undefined,
        coordinates:
          destination.coordinates ||
          (destination.latitude != null && destination.longitude != null
            ? { lat: destination.latitude, lng: destination.longitude }
            : null),
        date: destination.date || null
      },

      // === PRIX & PAIEMENT (CORRIGÉ) ===
      pricing: {
        amount: pricing.total ?? pricing.amount ?? 0, // Utilise 'total' en priorité, sinon 'amount'
        deliveryFee: pricing.deliveryFee ?? 0,
        serviceFee: pricing.serviceFee ?? 0,
        total: pricing.total ?? 0,
        currency: pricing.currency || 'XOF',
        distance: apiData.distanceKm ?? pricing.distance ?? null // Récupère distanceKm depuis la racine
      },
      payment: {
        status: payment.status || 'pending',
        method:
          payment.method == 'mobileMoney'
            ? 'mobile_money'
            : payment.method || '',
        transactionId: payment.transactionId || ''
      },

      // === DATES & TIMELINES ===
      createdAt: apiData.createdAt,
      updatedAt: apiData.updatedAt,
      timeline: timeline,
      checkpoints: checkpoints,
      steps: stepsTimeline,

      // === FILET DE SÉCURITÉ ===
      _raw: apiData,

      // === ESCROW / LIEN DE PAIEMENT ===
      metadata: {
        merchandisePrice: apiData.metadata?.merchandisePrice || 0,
        deliveryFeePayer: apiData.metadata?.deliveryFeePayer || 'sender',
        paymentLinkEnabled: apiData.metadata?.paymentLinkEnabled || false,
        // Préserver locationsFull pour les lectures futures (ex: pré-remplissage du wizard)
        locationsFull: apiData.metadata?.locationsFull || null
      },

      srcDstLocation: {
        srcPosition: {
          coordinates:
            pickup.coordinates ||
            (pickup.latitude != null && pickup.longitude != null
              ? { lat: pickup.latitude, lng: pickup.longitude }
              : undefined),
          address: pickup.address,
          locationText: pickup.locationText || pickup.address,
          city: pickup.city,
          // Description : champ direct API → fallback locationsFull.pickup
          description: pickup.description || pickupFull.description || '',
          contactName: pickup.contactName,
          contactPhone: pickup.contactPhone || pickup.phone
        },
        dstPosition: {
          coordinates:
            destination.coordinates ||
            (destination.latitude != null && destination.longitude != null
              ? { lat: destination.latitude, lng: destination.longitude }
              : undefined),
          address: destination.address,
          locationText: destination.locationText || destination.address,
          city: destination.city,
          // Description : champ direct API → fallback locationsFull.destination
          description: destination.description || destinationFull.description || '',
          contactName: destination.contactName,
          contactPhone: destination.contactPhone || destination.phone
        },
        // Utiliser les checkpoints déjà mappés (avec description + fallback locationsFull)
        // et les convertir dans le format attendu par le wizard
        checkpoints: checkpoints.map((cp, i) => ({
          address: cp.address || '',
          coordinates:
            cp.lat != null && cp.lng != null
              ? { lat: cp.lat, lng: cp.lng }
              : cp.latitude != null && cp.longitude != null
                ? { lat: cp.latitude, lng: cp.longitude }
                : undefined,
          // Description : déjà résolue avec fallback dans le map checkpoints ci-dessus
          description: cp.description || checkpointsFull[i]?.description || '',
          contactName: cp.contactName || cp.name || '',
          contactPhone: cp.phone || cp.contactPhone || ''
        }))
      }
    };
  }

  /**
   * Deserialize fees calculation response
   * GET /package/calculate/fee → { statusCode: 200, data: { baseFee, total, ... } }
   */
  deserializeFeesCalculation(raw) {
    const apiResponse = raw?.data || raw;
    const isOk =
      apiResponse?.statusCode === 200 || apiResponse?.success === true;

    if (!isOk) {
      throw new Error('Fees calculation failed');
    }

    const data = apiResponse.data || apiResponse;

    const distanceKm = data.distanceKm || 0;
    const durationMin = data.durationMin || 0;

    return {
      // Format API
      baseFee: data.baseFee || 0,
      pickupFee: data.pickupFee || 0,
      distanceFee: data.distanceFee || 0,
      weightFee: data.weightFee || 0,
      documentFee: data.documentFee || 0,
      checkpointFee: data.checkpointFee || 0,
      originalDeliveryFee: data.originalDeliveryFee || 0,
      deliveryFee: data.deliveryFee || 0,
      serviceFee: data.serviceFee || 0,
      discount: data.discount || 0,
      customerDeliveryCost: data.customerDeliveryCost || 0,
      merchantDeliveryCost: data.merchantDeliveryCost || 0,
      merchantPayout: data.merchantPayout || 0,
      commissionRapidooRider: data.commissionRapidooRider || 0,
      riderPayout: data.riderPayout || 0,
      rapidooRevenue: data.rapidooRevenue || 0,
      total: data.total ?? data.totalAmount ?? 0,
      totalAmount: data.total ?? data.totalAmount ?? 0,
      distanceKm: distanceKm,
      durationMin: durationMin,
      weight: data.weight || 0,
      weightInGrams: data.weightInGrams || 0,
      weightUnit: data.weightUnit || 'g',
      packageValue: data.packageValue || 0,
      checkpointCount: data.checkpointCount || 0,
      isDocument: Boolean(data.isDocument),
      freeDeliveryThreshold: data.freeDeliveryThreshold || 100000,
      freeDeliveryApplied: Boolean(data.freeDeliveryApplied),
      currency: data.currency || 'XOF',
      distanceFormatted:
        data.distanceFormatted || `${distanceKm.toFixed(1)} km`,
      durationFormatted:
        data.durationFormatted || `${Math.round(durationMin)} min`,
      weightFormatted: data.weightFormatted || '',
      merchandisePrice: data.merchandisePrice || 0,
      deliveryFeePayer: data.deliveryFeePayer || 'sender',
      merchantPaysDelivery: Boolean(data.merchantPaysDelivery),

      // Aliases pour compatibilité UI
      deliveryCost: data.deliveryFee || 0,
      distanceCost: data.distanceFee || 0,
      totalDistance: distanceKm,
      totalDuration: durationMin,
      weightSurcharge: data.weightFee || 0,
      paymentFee: data.serviceFee || 0
    };
  }

  /**
   * Deserialize delivery creation response
   * POST /package/create → { statusCode: 201, data: { id, reference, ... } }
   */
  deserializeDeliveryCreation(raw) {
    const apiResponse = raw?.data || raw;
    const isOk =
      apiResponse?.statusCode === 201 ||
      apiResponse?.statusCode === 200 ||
      apiResponse?.success === true;

    if (!isOk) {
      throw new Error('Delivery creation failed');
    }

    const pkg = apiResponse.data || apiResponse;

    return this.deserializePackage(pkg);
  }

  /**
   * Deserialize error response
   */
  deserializeError(apiError) {
    if (!apiError) {
      return { message: 'Une erreur est survenue', code: 'UNKNOWN_ERROR' };
    }

    return {
      message:
        apiError.message ||
        apiError.data?.message ||
        apiError.error?.message ||
        'Une erreur est survenue',
      code:
        apiError.code ||
        apiError.data?.code ||
        apiError.error?.code ||
        'API_ERROR',
      details: apiError.details || apiError.data?.details || null
    };
  }
}

// Export singleton instance
const packageSerializer = new PackageSerializer();
export default packageSerializer;

/*
 * ═══════════════════════════════════════════════════════════════════════════
 * NOTE — Champs récupérés par le client mais NON exposés par l'API v1
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Les champs suivants étaient disponibles dans l'ancienne architecture
 * ou sont manipulés en UI, mais l'endpoint /package n'y répond pas encore :
 *
 *  - packageType.nbDocs       → L'ancienne API distinguait les colis en
 *                                "documents" (nbDocs) des autres. La nouvelle
 *                                API utilise packageInfo.category = 'document'.
 *
 *  - parcelDetails.width      → Dimensions (width, height, length) : absents
 *  - parcelDetails.height       de la réponse API. Le wizard peut les collecter
 *  - parcelDetails.length       côté client mais le serveur ne les stocke pas.
 *
 *  - deliveryCode / trackingCode → L'API expose uniquement `reference` (ex:
 *                                   PKG-ylrf3tfv5cs1). Les deux alias pointent
 *                                   vers ce même champ.
 *
 *  - stepsTimeline.payment    → Déduit de la timeline (status = 'payment_paid').
 *                                La nouvelle API ne l'expose pas en champ propre.
 *
 *  - fees.totalDuration /     → L'API /calculate/fee ne retourne pas la durée
 *    fees.durationFormatted     estimée du trajet, seulement la distance.
 *
 * Si ces données deviennent nécessaires, il faudra demander une extension de l'API.
 * ═══════════════════════════════════════════════════════════════════════════
 */
